import React, { useState, useEffect } from "react";

// Component to fetch and display question images
function QuestionImageViewer({ questionId, onDelete }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/questions/${questionId}/images`
                );
                
                if (response.ok) {
                    const data = await response.json();
                    setImages(data.images || []);
                }
            } catch (error) {
                console.error('Error fetching question images:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchImages();
    }, [questionId]);

    if (loading) {
        return (
            <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-charcoal mb-3">Nuvarande frågbild</h3>
                <div className="w-full h-48 bg-gray-100 rounded-lg animate-pulse"></div>
            </div>
        );
    }

    const questionImage = images.find(img => img.image === 'question_image');
    
    if (!questionImage) return null;

    return (
        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-charcoal mb-3">Nuvarande frågbild</h3>
            <div className="relative group mb-3">
                <img
                    src={`data:image/jpeg;base64,${questionImage.base64}`}
                    alt="Frågbild"
                    className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
                <button
                    onClick={onDelete}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    title="Ta bort bild"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

/**
 * Question form component for quiz management
 * Handles question text, species selection, and answer inputs
 * 
 * @param {string} questionText - Question text value
 * @param {Function} setQuestionText - Setter for question text
 * @param {string} selectedSpecies - Selected species ID
 * @param {Function} setSelectedSpecies - Setter for selected species
 * @param {string} speciesSearchQuery - Species search query
 * @param {Function} setSpeciesSearchQuery - Setter for species search
 * @param {boolean} isSpeciesDropdownOpen - Dropdown open state
 * @param {Function} setIsSpeciesDropdownOpen - Setter for dropdown
 * @param {Array} filteredSpecies - Filtered species list
 * @param {Function} handleCheck - Handler for species selection
 * @param {Object} speciesDropdownRef - Ref for dropdown
 * @param {string} answer1 - Correct answer value
 * @param {Function} setAnswer1 - Setter for answer 1
 * @param {string} answer2 - Wrong answer 1 value
 * @param {Function} setAnswer2 - Setter for answer 2
 * @param {string} answer3 - Wrong answer 2 value
 * @param {Function} setAnswer3 - Setter for answer 3
 * @param {Function} handleRemoveImage - Handler to remove answer images
 * @param {Function} handleFileChange1 - File handler for answer 1
 * @param {Function} handleFileChange2 - File handler for answer 2
 * @param {Function} handleFileChange3 - File handler for answer 3
 * @param {Function} handleAudioFileChange - Handler for audio file
 * @param {File} audioFile - Selected audio file
 * @param {Object} selectedItem - Currently selected question
 * @param {Function} handleSave - Save handler
 * @param {Function} handleDeleteQuestion - Delete handler
 * @param {string} imageFileEncodedQ - Question image (base64)
 * @param {Function} handleFileChangeQ - Handler for question image
 * @param {Function} setImageFileEncodedQ - Setter for question image
 */
export default function QuestionForm({
    questionText,
    setQuestionText,
    selectedSpecies,
    setSelectedSpecies,
    speciesSearchQuery,
    setSpeciesSearchQuery,
    isSpeciesDropdownOpen,
    setIsSpeciesDropdownOpen,
    filteredSpecies,
    handleCheck,
    speciesDropdownRef,
    answer1,
    setAnswer1,
    answer2,
    setAnswer2,
    answer3,
    setAnswer3,
    handleRemoveImage,
    handleFileChange1,
    handleFileChange2,
    handleFileChange3,
    handleAudioFileChange,
    audioFile,
    selectedItem,
    handleSave,
    handleDeleteQuestion,
    handleDeleteQuestionImage,
    imageFileEncodedQ,
    handleFileChangeQ,
    setImageFileEncodedQ
}) {
    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="font-heading text-2xl font-bold text-charcoal">
                    {selectedItem ? "Redigera fråga" : "Lägg till ny fråga"}
                </h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                    {/* Question Text Input */}
                    <div>
                        <label className="block text-sm font-semibold text-charcoal mb-2">
                            Fråga *
                        </label>
                        <input
                            type="text"
                            placeholder="Ange frågetext"
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                        />
                    </div>

                    {/* Species Select */}
                    <div className="relative" ref={speciesDropdownRef}>
                        <label className="block text-sm font-semibold text-charcoal mb-2">
                            Välj art
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Sök eller välj art..."
                                value={speciesSearchQuery}
                                onChange={(e) => {
                                    setSpeciesSearchQuery(e.target.value);
                                    setIsSpeciesDropdownOpen(true);
                                }}
                                onFocus={() => setIsSpeciesDropdownOpen(true)}
                                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                            />
                            <button
                                type="button"
                                onClick={() => setIsSpeciesDropdownOpen(!isSpeciesDropdownOpen)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                            >
                                <svg 
                                    className={`w-5 h-5 transition-transform ${isSpeciesDropdownOpen ? 'rotate-180' : ''}`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            
                            {/* Dropdown List */}
                            {isSpeciesDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {filteredSpecies.length > 0 ? (
                                        <>
                                            <div 
                                                onClick={() => {
                                                    setSelectedSpecies("");
                                                    setSpeciesSearchQuery("");
                                                    setIsSpeciesDropdownOpen(false);
                                                }}
                                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-500 italic border-b border-gray-200"
                                            >
                                                Ingen art vald
                                            </div>
                                            {filteredSpecies.map((sp) => (
                                                <div
                                                    key={sp.id}
                                                    onClick={() => handleCheck(sp.id.toString())}
                                                    className={`px-4 py-2 hover:bg-sage cursor-pointer transition-colors ${
                                                        selectedSpecies === sp.id.toString() ? 'bg-sage font-semibold' : ''
                                                    }`}
                                                >
                                                    {sp.name}
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="px-4 py-3 text-gray-500 text-center">
                                            Ingen art hittades
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Answer Options */}
                    <div>
                        <label className="block text-sm font-semibold text-charcoal mb-2">
                            Svarsalternativ
                        </label>
                        
                        {/* Correct Answer */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-green-600">✓ RÄTT SVAR</span>
                            </div>
                            {answer1 && answer1.startsWith("data:image") ? (
                                <div className="relative">
                                    <img alt="Answer 1" className="w-full h-24 object-cover rounded-lg" src={answer1} />
                                    <button
                                        onClick={() => handleRemoveImage("answer 1")}
                                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                        Radera
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Rätt svar"
                                        value={answer1}
                                        onChange={(e) => setAnswer1(e.target.value)}
                                        className="flex-1 px-4 py-3 border-2 border-green-300 bg-green-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                    <label htmlFor="answer1-file" className="flex-shrink-0">
                                        <input
                                            type="file"
                                            name="imageUpload"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            id="answer1-file"
                                            onChange={handleFileChange1}
                                        />
                                        <button
                                            type="button"
                                            className="bg-forest hover:bg-leaf text-white px-3 py-3 rounded-lg transition-colors"
                                            onClick={() => document.getElementById('answer1-file').click()}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            </svg>
                                        </button>
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Wrong Answer 1 */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-red-600">✗ FEL SVAR 1</span>
                            </div>
                            {answer2 && answer2.startsWith("data:image") ? (
                                <div className="relative">
                                    <img alt="Answer 2" className="w-full h-24 object-cover rounded-lg" src={answer2} />
                                    <button
                                        onClick={() => handleRemoveImage("answer 2")}
                                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                        Radera
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Fel svar 1"
                                        value={answer2}
                                        onChange={(e) => setAnswer2(e.target.value)}
                                        className="flex-1 px-4 py-3 border-2 border-red-300 bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                    <label htmlFor="answer2-file" className="flex-shrink-0">
                                        <input
                                            type="file"
                                            name="imageUpload"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            id="answer2-file"
                                            onChange={handleFileChange2}
                                        />
                                        <button
                                            type="button"
                                            className="bg-forest hover:bg-leaf text-white px-3 py-3 rounded-lg transition-colors"
                                            onClick={() => document.getElementById('answer2-file').click()}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            </svg>
                                        </button>
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Wrong Answer 2 */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-red-600">✗ FEL SVAR 2</span>
                            </div>
                            {answer3 && answer3.startsWith("data:image") ? (
                                <div className="relative">
                                    <img alt="Answer 3" className="w-full h-24 object-cover rounded-lg" src={answer3} />
                                    <button
                                        onClick={() => handleRemoveImage("answer 3")}
                                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                        Radera
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Fel svar 2"
                                        value={answer3}
                                        onChange={(e) => setAnswer3(e.target.value)}
                                        className="flex-1 px-4 py-3 border-2 border-red-300 bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                    <label htmlFor="answer3-file" className="flex-shrink-0">
                                        <input
                                            type="file"
                                            name="imageUpload"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            id="answer3-file"
                                            onChange={handleFileChange3}
                                        />
                                        <button
                                            type="button"
                                            className="bg-forest hover:bg-leaf text-white px-3 py-3 rounded-lg transition-colors"
                                            onClick={() => document.getElementById('answer3-file').click()}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            </svg>
                                        </button>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Current Question Image from Database */}
                    {selectedItem && selectedItem.question_image && selectedItem.question_image !== 'null' && !imageFileEncodedQ && (
                        <QuestionImageViewer 
                            questionId={selectedItem.id}
                            onDelete={handleDeleteQuestionImage}
                        />
                    )}

                    {/* Current Audio from Database */}
                    {selectedItem && selectedItem.audio && selectedItem.audio !== 'null' && !audioFile && (
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="font-semibold text-charcoal mb-3">Nuvarande ljudspår</h3>
                            <div className="bg-purple-50 rounded-lg p-3 mb-3">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="bg-purple-600 rounded-full p-2">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-purple-800 font-medium truncate flex-1">
                                        {selectedItem.audio}
                                    </span>
                                </div>
                                <audio 
                                    controls 
                                    className="w-full h-10"
                                    src={`${process.env.REACT_APP_API_URL}/api/questions/${selectedItem.id}/media/${selectedItem.audio}`}
                                >
                                    Din webbläsare stödjer inte ljuduppspelning.
                                </audio>
                            </div>
                        </div>
                    )}

                    {/* New Question Media Preview */}
                    {(imageFileEncodedQ || audioFile) && (
                        <div className="border-t border-gray-200 pt-4">
                            <label className="block text-sm font-semibold text-charcoal mb-2">
                                {selectedItem ? "Ny media (ersätter nuvarande)" : "Media för frågan"}
                            </label>
                            {imageFileEncodedQ && (
                                <div className="mb-3 relative">
                                    <img alt="Question" className="w-full h-48 object-cover rounded-lg" src={imageFileEncodedQ} />
                                    <button
                                        onClick={() => {
                                            setImageFileEncodedQ(null);
                                        }}
                                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                        Ta bort bild
                                    </button>
                                </div>
                            )}
                            {audioFile && (
                                <div className="mb-3">
                                    <audio controls className="w-full" src={URL.createObjectURL(audioFile)} />
                                    <button
                                        onClick={() => handleAudioFileChange({ target: { files: [] } })}
                                        className="mt-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                    >
                                        Ta bort ljud
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Media Upload Buttons */}
                    <div className="grid grid-cols-2 gap-3 pt-4">
                        <label htmlFor="image-file">
                            <input
                                type="file"
                                name="imageUpload"
                                accept="image/*"
                                style={{ display: "none" }}
                                id="image-file"
                                onChange={handleFileChangeQ}
                            />
                            <button
                                type="button"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                                onClick={() => document.getElementById('image-file').click()}
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                </svg>
                                Lägg till Bild
                            </button>
                        </label>
                        
                        <label htmlFor="audio-file">
                            <input
                                type="file"
                                name="audioUpload"
                                accept="audio/*"
                                style={{ display: "none" }}
                                id="audio-file"
                                onChange={handleAudioFileChange}
                            />
                            <button
                                type="button"
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                                onClick={() => document.getElementById('audio-file').click()}
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                                Lägg till Ljud
                            </button>
                        </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            onClick={handleSave}
                            className="w-full bg-forest hover:bg-leaf text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300"
                        >
                            {selectedItem ? "Spara ändringar" : "Lägg till fråga"}
                        </button>
                        
                        {selectedItem && (
                            <button
                                onClick={handleDeleteQuestion}
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300"
                            >
                                Radera fråga
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
